<template>
  <div v-if="isPopperShown" ref="installCertificateRef" class="install-certificate">
    <a-modal
      :get-container="() => ($refs.installCertificateRef as HTMLElement)"
      :visible="isPopperShown"
      :footer="null"
      :body-style="{ padding: 0 }"
      centered
      @cancel="() => onInstallCertPopperClosed(false)"
    >
      <template #closeIcon>
        <img class="icon-close" src="@assets/svgs/preload-icon-popper-close.svg" alt="" />
      </template>
      <div class="title">{{ $t('owork_base.certificate.title') }}</div>
      <InstallCertificateContent
        :positive-button-disabled="positiveButtonDisabled"
        @installation-confirmed="onInstallCertPopperClosed"
        @download-certificate="downloadCertificate"
      />
    </a-modal>
  </div>
</template>

<script lang="ts" setup>
  import { reactive, ref, toRef, toRefs, onMounted } from 'vue'
  import InstallCertificateContent from './InstallCertificateContent.vue'
  import { INSTANCE as WindowPopperManager } from '@/core/WindowPopperManager'
  import { getSmfServerApiBaseUrl } from '@/BuildConfig'
  import { INSTANCE as ToastManager } from '@/core/ToastManager'
  import { saveLinkAsFile, transmissionTaskPool, URLBuilder } from '@/core/Utils'
  import { INSTANCE as DeviceManager } from '@/core/devices/DeviceManager'
  import { AvailableType } from '@core/devices/IDevice'
  import { useOtrack } from '@core/compositions/Otrack'
  import { TrackEventMap } from '@/core/TrackConstants'

  const props = defineProps<{ bindingDeviceId: string }>()
  const { bindingDeviceId } = toRefs(props)
  const { track } = useOtrack()

  const isPopperShown = ref<boolean>(true)

  const PATH_DOWNLOAD_CERTIFICATE_SERVICE = 'web/downloadCertificate'
  const installCertificateUrl = new URLBuilder(getSmfServerApiBaseUrl())
    .appendPath(PATH_DOWNLOAD_CERTIFICATE_SERVICE)
    .toString()
  const enum DeviceHttpsAvailableCheckResult {
    REACHABLE,
    UNREACHABLE,
    DEVICE_NOT_FOUND,
  }

  interface ICheckProcess {
    isChecking: boolean
    start: () => void
    end: () => void
  }

  const checkDeviceAvailableWithHttps = async (
    deviceId: string | undefined,
    checkProcess: ICheckProcess
  ): Promise<DeviceHttpsAvailableCheckResult> => {
    checkProcess.start()

    // TODO: 与传输列表部分相关判断合并
    if (!deviceId) {
      checkProcess.end()

      return DeviceHttpsAvailableCheckResult.DEVICE_NOT_FOUND
    }

    const device = DeviceManager.getDeviceById(deviceId)

    if (!device) {
      checkProcess.end()

      return DeviceHttpsAvailableCheckResult.DEVICE_NOT_FOUND
    }

    const lanAvailable = await device.getLanAvailableInfo(true)

    checkProcess.end()

    return lanAvailable === AvailableType.REACHABLE
      ? DeviceHttpsAvailableCheckResult.REACHABLE
      : DeviceHttpsAvailableCheckResult.UNREACHABLE
  }

  const downloadCertificate = () => {
    transmissionTaskPool.addTask(() => {
      saveLinkAsFile(installCertificateUrl, 'certificate.cer')
    })
  }

  const closeAllInstallPopper = () => {
    isPopperShown.value = false
    WindowPopperManager.close()
  }

  const checkDeviceAvailableProcess = reactive<ICheckProcess>({
    isChecking: false,
    start() {
      this.isChecking = true
    },
    end() {
      this.isChecking = false
    },
  })
  const positiveButtonDisabled = toRef(checkDeviceAvailableProcess, 'isChecking')

  const onInstallCertPopperClosed = async (checkFailedKeepDialog?: boolean) => {
    if (checkDeviceAvailableProcess.isChecking && checkFailedKeepDialog) return

    if (!checkFailedKeepDialog) {
      closeAllInstallPopper()
    }
    const checkAvailable = await checkDeviceAvailableWithHttps(bindingDeviceId.value, checkDeviceAvailableProcess)

    track(
      { eventId: TrackEventMap.CERTIFICATE_INSTALLATION_OPERATION },
      {
        operation: checkFailedKeepDialog ? 'installation_complete' : 'close',
        isInstallSucceed: checkAvailable === DeviceHttpsAvailableCheckResult.REACHABLE,
      }
    )

    switch (checkAvailable) {
      case DeviceHttpsAvailableCheckResult.REACHABLE:
        closeAllInstallPopper()
        break
      case DeviceHttpsAvailableCheckResult.UNREACHABLE:
        ToastManager.show('owork_base.certificate.failed')

        break
      case DeviceHttpsAvailableCheckResult.DEVICE_NOT_FOUND:
        closeAllInstallPopper()
        break
      default:
        closeAllInstallPopper()
        break
    }
  }

  onMounted(() => {
    downloadCertificate()
  })
</script>

<style lang="stylus" scoped>

  .install-certificate
    :deep(.ant-modal)
      width content_modal_width
      .ant-modal-content
        background rgba(255, 255, 255, 1) !important
        border-radius 12px !important
    .icon-close
      icon-popper-close()
    .title
      display inline-block
      title()
      rect(100%, 36px)
      line-height 26px
      font-size 20px
      margin-top 23px
      text-align center
</style>
